import React from 'react';

const ClientResults = () => {
  const results = [
    {
      title: "Reliable systems",
      description: "Fewer errors and less overhead from a cleaner operating foundation.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Automated workflows",
      description: "Repetitive tasks removed and data consistency improved across teams.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
      )
    },
    {
      title: "Connected tools",
      description: "CRMs, internal systems, and third-party services working in sync.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Custom internal tools",
      description: "Bespoke utilities for reporting, operations, and day-to-day execution.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    {
      title: "Documentation + handoff",
      description: "SOPs and training that keep systems stable long after implementation.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
  ];

  return (
    <section className="border-t border-slate-200/60 bg-slate-950 text-white">
      <div className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-24">
        <div className="grid gap-10 md:grid-cols-12 md:items-start">
          {/* Left: Title */}
          <div className="md:col-span-5">
            <p className="text-xs font-medium tracking-[0.18em] text-slate-300/80">
              RESULTS
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl text-white">
              What clients see after working with us
            </h2>
            <p className="mt-4 max-w-md text-base leading-7 text-slate-200/80">
              Outcomes that make systems easier to operate, easier to trust, and easier to scale.
            </p>
          </div>

          {/* Right: Visualization */}
          <div className="md:col-span-7">
            {/* Before → After bar */}
            <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3">
              <div className="flex items-center justify-between text-sm text-slate-200/80">
                <span className="font-medium">Before</span>
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span className="font-medium">After</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-4 text-xs text-slate-300/70">
                <div className="space-y-1">
                  <p>Fragmented tools</p>
                  <p>Manual busywork</p>
                  <p>Unclear ownership</p>
                </div>
                <div className="space-y-1 text-right">
                  <p>Unified systems</p>
                  <p>Automated workflows</p>
                  <p>Documented handoff</p>
                </div>
              </div>
            </div>

            {/* Result cards */}
            <div className="grid gap-4 sm:grid-cols-2">
              {results.map(({ title, description, icon }) => (
                <div
                  key={title}
                  className="group rounded-2xl border border-slate-800 bg-slate-900/30 p-5 transition-colors hover:bg-slate-900/45"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-2 text-slate-200">
                      {icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold tracking-tight text-white">
                        {title}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-200/75">
                        {description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Credibility note */}
            <p className="mt-6 text-xs text-slate-300/60">
              Delivered through systems implementation, automation, integrations, and clean documentation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientResults;
